import React, { useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import CeAssessmentIntroCard from './CeAssessmentIntroCard';
import CeAssessmentProgress from './CeAssessmentProgress';
import CeAssessmentQuestion from './CeAssessmentQuestion';
import CeAssessmentResults from './CeAssessmentResults';
import CeCertificatePreview from './CeCertificatePreview';

// GraphQL Query for CE Assessment
const GET_CE_ASSESSMENT = gql`
  query GetCeAssessment($planTemplateId: ID!) {
    planCeAssessment(planTemplateId: $planTemplateId) {
      id
      surveyId
      planTemplateId
      title
      description
      certificateId
      ceCreditHours
      passingThreshold
      questionsCount
      totalQuestionPool
      questions {
        id
        text
        questionType
        learningObjective
        options {
          id
          text
        }
        correctAnswerText
      }
      userAttemptStatus {
        hasAttempted
        hasPassed
        attemptsCount
        bestScore
        lastAttemptAt
        ceCreditEarnedAt
        ceCreditHours
      }
    }
  }
`;

// GraphQL Mutation for submitting assessment
const SUBMIT_CE_ASSESSMENT = gql`
  mutation SubmitCeAssessment(
    $planTemplateId: ID!
    $userPlanId: ID
    $answers: [CeAssessmentAnswerInput!]!
  ) {
    submitCeAssessment(
      planTemplateId: $planTemplateId
      userPlanId: $userPlanId
      answers: $answers
    ) {
      success
      passed
      score
      correctCount
      totalQuestions
      passingThreshold
      certificateId
      vaultSaved
      ceCredit {
        id
        graded
        passed
        watched
      }
      questionResults {
        questionId
        questionText
        selectedOptionId
        selectedOptionText
        correctOptionId
        correctOptionText
        isCorrect
        learningObjective
      }
      errors
    }
  }
`;

/**
 * CeAssessmentContainer - Main orchestrator for CE assessment flow
 * 
 * Manages assessment state (intro, in_progress, grading, results) and
 * integrates with GraphQL for data fetching and submission.
 */
const CeAssessmentContainer = ({
  planTemplateId,
  userPlanId,
  userName,
  onComplete,
  onSkip,
  className = '',
}) => {
  const intl = useIntl();
  // Assessment state: 'intro' | 'in_progress' | 'grading' | 'results' | 'already_passed'
  const [state, setState] = useState('intro');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [initialCheckDone, setInitialCheckDone] = useState(false);

  // Fetch CE assessment data
  const { data, loading, error, refetch } = useQuery(GET_CE_ASSESSMENT, {
    variables: { planTemplateId },
    fetchPolicy: 'cache-and-network',
    onCompleted: (queryData) => {
      // Check if user has already passed and set state accordingly
      if (!initialCheckDone && queryData?.planCeAssessment?.userAttemptStatus?.hasPassed) {
        setState('already_passed');
        setInitialCheckDone(true);
      }
    },
  });

  // Submit assessment mutation
  const [submitAssessment, { loading: submitting }] = useMutation(SUBMIT_CE_ASSESSMENT);

  const assessment = data?.planCeAssessment;
  const questions = assessment?.questions || [];
  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const hasAnsweredCurrent = currentQuestion && !!userAnswers[currentQuestion.id];

  // Memoize estimated time
  const estimatedMinutes = useMemo(() => {
    return Math.ceil(questions.length * 1); // ~1 min per question
  }, [questions.length]);

  // Handler: Start assessment
  const handleStart = () => {
    setState('in_progress');
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setResult(null);
  };

  // Handler: Answer a question
  const handleAnswer = (optionId) => {
    if (!currentQuestion) return;
    
    setUserAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: optionId,
    }));
    
    // Auto-advance to next question after a brief delay
    // Using functional update to avoid stale closure issue
    setTimeout(() => {
      setCurrentQuestionIndex(prevIndex => {
        const isLast = prevIndex === questions.length - 1;
        if (!isLast) {
          return prevIndex + 1;
        }
        return prevIndex;
      });
    }, 500);
  };

  // Handler: Go to next question (or submit if last)
  const handleNext = async () => {
    if (isLastQuestion) {
      // Submit the assessment
      setState('grading');
      
      try {
        // Build answers array for GraphQL
        const answersArray = Object.entries(userAnswers).map(([questionId, optionId]) => ({
          questionId,
          selectedOptionId: optionId,
        }));

        const { data: submitData } = await submitAssessment({
          variables: {
            planTemplateId,
            userPlanId,
            answers: answersArray,
          },
        });

        const submitResult = submitData?.submitCeAssessment;
        
        if (submitResult?.errors?.length > 0) {
          console.error('Assessment submission errors:', submitResult.errors);
          // Still show results even with errors
        }

        setResult({
          passed: submitResult.passed,
          score: submitResult.score,
          correctAnswers: submitResult.correctCount,
          totalQuestions: submitResult.totalQuestions,
          passingThreshold: submitResult.passingThreshold,
          ceCredit: submitResult.ceCredit,
          questionResults: submitResult.questionResults,
          vaultSaved: submitResult.vaultSaved,
          completedAt: new Date().toISOString(),
          certificateId: submitResult.certificateId || null,
        });
        
        // Log vault save status for debugging
        if (submitResult.vaultSaved) {
          console.log('[CE Assessment] Certificate saved to vault successfully');
        } else if (submitResult.passed) {
          console.warn('[CE Assessment] Passed but certificate NOT saved to vault - check server logs');
        }
        
        setState('results');
        
        // NOTE: Do NOT call onComplete here - only call it when user clicks "Continue" 
        // after viewing their results. This ensures the results screen is displayed.
      } catch (err) {
        console.error('Error submitting assessment:', err);
        setState('in_progress'); // Return to last question
      }
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  // Handler: Go to previous question
  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  // Handler: Retry assessment
  const handleRetry = () => {
    setState('intro');
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setResult(null);
    refetch(); // Refetch to get new random questions
  };

  // Handler: Continue after results
  const handleContinue = () => {
    if (onComplete) {
      onComplete(result);
    }
  };

  // Loading state
  if (loading && !assessment) {
    return (
      <div className={`flex items-center justify-center min-h-[40vh] ${className}`}>
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-succession-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-succession-text-secondary">{intl.formatMessage({ id: 'succession.plans.ce_loading' })}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <p className="text-succession-error">Error loading assessment: {error.message}</p>
        <button
          onClick={() => refetch()}
          className="mt-4 px-4 py-2 text-succession-primary border border-succession-primary rounded-lg hover:bg-succession-primary/10 transition-all duration-300"
        >
          {intl.formatMessage({ id: 'succession.plans.ce_try_again' })}
        </button>
      </div>
    );
  }

  // No assessment available
  if (!assessment) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <p className="text-succession-text-secondary">{intl.formatMessage({ id: 'succession.plans.ce_not_available' })}</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-succession-bg-primary ${className}`}>
      {/* Intro State */}
      {state === 'intro' && (
        <div className="max-w-4xl mx-auto p-8">
          <CeAssessmentIntroCard
            creditHours={assessment.ceCreditHours}
            questionCount={assessment.questionsCount}
            estimatedMinutes={estimatedMinutes}
            passingThreshold={assessment.passingThreshold * 100}
            courseName={assessment.title}
            sponsorName={null}
            onStart={handleStart}
            onSkip={onSkip}
          />
        </div>
      )}

      {/* In Progress State */}
      {state === 'in_progress' && currentQuestion && (
        <div className="max-w-3xl mx-auto p-8 space-y-8">
          <CeAssessmentProgress
            currentQuestion={currentQuestionIndex + 1}
            totalQuestions={questions.length}
          />

          <CeAssessmentQuestion
            questionNumber={currentQuestionIndex + 1}
            totalQuestions={questions.length}
            questionText={currentQuestion.text}
            questionType={currentQuestion.questionType}
            options={currentQuestion.options}
            learningObjective={currentQuestion.learningObjective}
            correctAnswerText={currentQuestion.correctAnswerText}
            onAnswer={handleAnswer}
          />

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
              className="px-4 py-2 text-succession-text-secondary border border-succession-border hover:bg-succession-hover-bg rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {intl.formatMessage({ id: 'succession.plans.ce_previous' })}
            </button>

            <button
              onClick={handleNext}
              disabled={!hasAnsweredCurrent || submitting}
              className="px-6 py-2 font-medium text-white bg-succession-primary hover:bg-succession-primary/90 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLastQuestion ? intl.formatMessage({ id: 'succession.plans.ce_submit_assessment' }) : intl.formatMessage({ id: 'succession.plans.ce_next_question' })}
            </button>
          </div>
        </div>
      )}

      {/* Grading State */}
      {state === 'grading' && (
        <div className="flex items-center justify-center min-h-[70vh] p-8">
          <div className="text-center space-y-8 max-w-2xl">
            {/* Inline keyframes to avoid dependency on Tailwind animate-spin */}
            <style>{`@keyframes ce-spin{to{transform:rotate(360deg)}}`}</style>
            
            {/* Animated spinner in primary circle */}
            <div className="w-24 h-24 bg-succession-primary rounded-full flex items-center justify-center mx-auto shadow-lg">
              <div className="w-12 h-12" style={{ animation: 'ce-spin 1s linear infinite' }} aria-hidden="true">
                <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-30" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-90" fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-4a6 6 0 0 0-6-6V2z" />
                </svg>
              </div>
            </div>
            
            {/* Processing text */}
            <div className="space-y-3">
              <h1 className="text-3xl md:text-4xl font-bold text-succession-text-primary">
                {intl.formatMessage({ id: 'succession.plans.ce_grading_title' })}
              </h1>
              <p className="text-lg text-succession-text-secondary max-w-md mx-auto">
                {intl.formatMessage({ id: 'succession.plans.ce_grading_description' })}
              </p>
            </div>
            
            {/* Bouncing dots */}
            <div className="flex justify-center space-x-2">
              <div className="w-3 h-3 bg-succession-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-3 h-3 bg-succession-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-3 h-3 bg-succession-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        </div>
      )}

      {/* Results State */}
      {state === 'results' && result && (
        <div className="max-w-4xl mx-auto p-8">
          <CeAssessmentResults
            passed={result.passed}
            score={result.score}
            passingThreshold={result.passingThreshold}
            totalQuestions={result.totalQuestions}
            correctAnswers={result.correctAnswers}
            creditHours={assessment.ceCreditHours}
            courseName={assessment.title}
            completionDate={new Date(result.completedAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
            certificateId={result.certificateId}
            userName={userName}
            onRetry={handleRetry}
            onContinue={handleContinue}
            questionResults={result.questionResults}
          />
        </div>
      )}

      {/* Already Passed State - show when user returns after previously passing */}
      {state === 'already_passed' && (
        <div className="max-w-4xl mx-auto p-8 space-y-8">
          {/* Success Header */}
          <div className="text-center space-y-4">
            <div className="w-20 h-20 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <svg className="w-10 h-10 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-succession-text-primary">
                {intl.formatMessage({ id: 'succession.plans.ce_already_earned_title' })}
              </h2>
              <p className="text-succession-text-secondary max-w-md mx-auto">
                {intl.formatMessage({ id: 'succession.plans.ce_already_earned_description' }, { hours: assessment.ceCreditHours })}
              </p>
            </div>
          </div>

          {/* Certificate Preview */}
          <CeCertificatePreview
            userName={userName}
            courseName={assessment.title}
            creditHours={assessment.ceCreditHours}
            completionDate={
              assessment.userAttemptStatus?.ceCreditEarnedAt
                ? new Date(assessment.userAttemptStatus.ceCreditEarnedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : new Date().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
            }
            certificateId={assessment.certificateId || `CE-${planTemplateId}-${assessment.userAttemptStatus?.attemptsCount || 1}`}
            className="max-w-2xl mx-auto"
          />

          {/* Quick Stats - use explicit null check to avoid React rendering "0" */}
          {assessment.userAttemptStatus?.bestScore != null && assessment.userAttemptStatus.bestScore > 0 ? (
            <div className="flex justify-center">
              <div className="inline-flex items-center gap-6 bg-succession-bg-secondary border border-succession-border rounded-xl px-6 py-3">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{assessment.userAttemptStatus.bestScore.toFixed(0)}%</p>
                  <p className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.plans.ce_best_score' })}</p>
                </div>
                <div className="w-px h-10 bg-succession-border" />
                <div className="text-center">
                  <p className="text-2xl font-bold text-succession-text-primary">{assessment.userAttemptStatus.attemptsCount || 0}</p>
                  <p className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.plans.ce_attempts' }, { count: assessment.userAttemptStatus.attemptsCount || 0 })}</p>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

CeAssessmentContainer.propTypes = {
  planTemplateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  userPlanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userName: PropTypes.string,
  onComplete: PropTypes.func,
  onSkip: PropTypes.func.isRequired,
  className: PropTypes.string,
};

CeAssessmentContainer.defaultProps = {
  userPlanId: null,
  userName: 'Advisor',
  onComplete: null,
  className: '',
};

export default CeAssessmentContainer;

